import { useTheme } from '@mui/material/styles';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import LogOutIcon from '@/assets/icons/logout_deep_green.svg';
import FaqIcon from '@/assets/icons/support_faq.svg';
import ChatIcon from '@/assets/icons/chat-1.svg';
import UserIcon from '@/assets/icons/user_deep_green.svg';
import Logo from '@/assets/images/navbar_logo.svg';
import {
    CustomersStepTypeEnumPaths,
    ICustomersGetResponse,
    ICustomersUserProfileResponse
} from '@/shared/endpoints/customers';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import Dropdown, { type DropdownOptions } from '@/shared/ui/dropdown';
import { onboardBackground, onboardForceColor } from '../../helpers';
// import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
// import { GtmEvent } from '@/shared/types/enums';
import { selectIsOpenedHelper } from '@/store/slices/onboarding/selectors';
import AppLazyLoadedImg from '@/shared/components/app-lazy-loaded-img';
import { openModal } from '@/store/slices/v-t-s';
import useLogoutWithStoreReset from '../../../../shared/hooks/useResetStore';
import { useChatClassName } from '@/shared/hooks/useChatClassName';

const Navbar = memo(
    ({
        user,
        userProfile,
        headerRef,
        withDropdown = true,
        step,
        isLoggedIn = false
    }: {
        userProfile: ICustomersUserProfileResponse | null;
        user?: ICustomersGetResponse;
        headerRef?: React.RefObject<HTMLDivElement>;
        withDropdown?: boolean;
        step: string;
        isLoggedIn?: boolean;
    }) => {
        const theme = useTheme();
        const dispatch = useAppDispatch();
        const isOpenedHelper = useAppSelector(selectIsOpenedHelper);
        const { pathname } = useLocation();
        const [isScrolled, setIsScrolled] = useState(false);
        const [showNumber, setShowNumber] = useState(false);
        const logoutWithStoreReset = useLogoutWithStoreReset();

        const handleLogOut = useCallback(() => {
            localStorage.setItem('takeMoment', '');
            logoutWithStoreReset();
        }, [logoutWithStoreReset]);

        const background = useMemo(
            () => onboardBackground(!!user?.id, step as CustomersStepTypeEnumPaths.ABOUT_YOU),
            [user?.id, step]
        );

        const forceBackground = useMemo(
            () => {
                return onboardForceColor(step as CustomersStepTypeEnumPaths.ABOUT_YOU);
            }
            , [step]);

        const userOptions = useMemo(
            (): DropdownOptions => [
                {
                    label: 'Log Out',
                    icon: <img src={LogOutIcon} alt="" />,
                    onClick: handleLogOut
                }
            ],
            [handleLogOut]
        );

        const chatClassName = useChatClassName();

        const supportOptions: DropdownOptions = useMemo(() => {
            const options = [
                {
                    label: 'FAQs',
                    icon: <img src={FaqIcon} alt="" />,
                    link: 'https://medbox.com/faqs/'
                }
                // {
                //     label: 'Schedule a Call',
                //     icon: <img src={PhoneIcon} alt="" />,
                //     link: 'https://medbox.com/book/'
                // }
            ] as DropdownOptions;

            if (user) {
                options.push({
                    label: 'Chat with Us',
                    icon: <img src={ChatIcon} width={16} height={16} alt="" />,
                    link: '',
                    classNames: chatClassName,
                })
            }

            return options;
        }, [user, chatClassName])


        useEffect(() => {
            const scrolllFunc = () => {
                setIsScrolled(window.scrollY > 0);
            };

            window.addEventListener('scroll', scrolllFunc);
            return () => {
                window.removeEventListener('scroll', scrolllFunc);
            };
        }, []);

        const dropdownColor = useMemo(() => {
            if (pathname.includes('login') || pathname.includes('signup')) {
                return 'rgba(255,255,255,0.8)';
            }
            return '#fff';
        }, [pathname]);

        const isProduction = import.meta.env.VITE_APP_IS_PRODUCTION === 'true';
        return (
            <div
                ref={headerRef}
                className={`fixed left-0 top-0 w-full ${isScrolled ? 'shadow-onboardNavbar' : ''} py-2 duration-100 md:py-5.5 ${isOpenedHelper ? 'z-50' : 'z-150'} `}
                style={{
                    background: (isScrolled || forceBackground) ? background || '#F4F5F8' : 'transparent'
                }}
            >
                <div className="container flex items-center justify-between">
                    {isProduction ? (
                        <Link to="/" className="h-max">
                            <AppLazyLoadedImg src={Logo} alt="" className="w-[90px] sm:w-28 md:w-auto" />
                        </Link>
                    ) : (
                        <div
                            onClick={() => {
                                dispatch(openModal());
                            }}
                        >
                            <AppLazyLoadedImg src={Logo} alt="" className="w-[90px] sm:w-28 md:w-auto" />
                        </div>
                    )}

                    {withDropdown && (
                        <div className="flex flex-wrap items-center justify-end gap-3 md:min-h-[2.2rem] md:justify-start">
                            <Dropdown
                                testId="navbar-support-dropdown"
                                options={supportOptions}
                                triggerSx={{
                                    minWidth: '190px',
                                    [theme.breakpoints.down('md')]: {
                                        width: 'max-content',
                                        minWidth: 'unset'
                                    },
                                    [theme.breakpoints.down('lg')]: {
                                        padding: '5px 12px'
                                    },
                                    maxHeight: '47px',

                                    bgcolor: dropdownColor
                                }}
                                mobileTitle="Support"
                                onClose={() => setShowNumber(false)}
                            >
                                <p className="pr-3 md:pr-0">Support</p>
                            </Dropdown>
                            {isLoggedIn && (
                                <Dropdown
                                    testId="navbar-user-dropdown"
                                    options={userOptions}
                                    triggerSx={{
                                        maxWidth: '100%',
                                        [theme.breakpoints.down('md')]: {
                                            minWidth: 'max-content',
                                            padding: '4px'
                                        },
                                        [theme.breakpoints.down('lg')]: {
                                            padding: '4px 12px'
                                        },
                                        maxHeight: '47px'
                                    }}
                                >
                                    <UserLabel firstName={userProfile?.firstName || ''} lastName={userProfile?.lastName || ''} />
                                </Dropdown>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
);

const UserLabel = ({ firstName = '', lastName = '' }: { firstName?: string; lastName?: string }) => {
    return (
        <>
            <div className="hidden items-center gap-2 pr-2.5 md:flex md:min-h-[2.2rem]">
                <img src={UserIcon} alt="user dropdown menu" aria-labelledby="user dropdown" />
                {!!firstName && !!lastName && <span className="ph-no-capture">{firstName + ' ' + lastName}</span>}
            </div>
            <div
                className={`flex items-center justify-center rounded-full text-base md-max:text-sm ${!!firstName && !!lastName ? 'bg-primary-400' : ''} bg-primary-400] mr-3 h-[26px] w-[26px] font-dmSans font-medium uppercase text-white md:hidden lg:text-lg`}
            >
                {!!firstName && !!lastName ? (
                    <span className="ph-no-capture">{firstName[0] + lastName[0]}</span>
                ) : (
                    <img src={UserIcon} alt="user dropdown menu" aria-labelledby="user dropdown icon" />
                )}
            </div>
        </>
    );
};

export default Navbar;